/**
 * Luật thuần của màn cấu hình loại chứng từ: chuẩn hoá mã, lọc danh sách, dò cột lọc đã dùng, tính
 * thân PATCH chỉ gồm trường đổi. Không dựng chuỗi hiển thị: trả mã để view tự dịch.
 */
import type { DenormTarget, DocumentType, OcrField, OcrSchemaListItem } from '../types';

/** Mã loại chứng từ backend nhận: chữ hoa, số, `_`, `-`, tối đa 50 ký tự. */
export const MA_HOP_LE = /^[A-Z0-9_-]+$/;
export const MA_TOI_DA = 50;
/** Khoá trường, bảng, cột: bắt đầu bằng chữ cái, rồi chữ, số, `_`. */
export const KHOA_HOP_LE = /^[a-zA-Z][a-zA-Z0-9_]*$/;

/** Gõ tới đâu chuẩn hoá tới đó: bỏ dấu, viết hoa, khoảng trắng thành `-`, bỏ ký tự lạ. */
export function chuanHoaMa(s: string): string {
  return s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/đ/gi, 'D')
    .toUpperCase()
    .replace(/\s+/g, '-')
    .replace(/[^A-Z0-9_-]/g, '')
    .slice(0, MA_TOI_DA);
}

export type LoiMa = 'trong' | 'sai';
export function kiemMa(ma: string): LoiMa | null {
  if (!ma) return 'trong';
  return MA_HOP_LE.test(ma) ? null : 'sai';
}

export type LoiKhoa = 'trong' | 'sai';
export function kiemKhoa(khoa: string): LoiKhoa | null {
  if (!khoa.trim()) return 'trong';
  return KHOA_HOP_LE.test(khoa.trim()) ? null : 'sai';
}

function khongDau(s: string): string {
  return s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/đ/gi, 'd')
    .toLowerCase();
}

export type LocTrangThai = 'tat-ca' | 'bat' | 'tat';

/** Lọc ở client: tìm theo mã, tên, mô tả không phân biệt dấu; loại; đang dùng hay đã tắt. */
export function locLoaiChungTu(
  ds: readonly OcrSchemaListItem[],
  { tuKhoa, loai, trangThai }: { tuKhoa: string; loai?: DocumentType; trangThai: LocTrangThai },
): OcrSchemaListItem[] {
  const q = khongDau(tuKhoa.trim());
  return ds.filter(
    (s) =>
      (!loai || s.type === loai) &&
      (trangThai === 'tat-ca' || (trangThai === 'bat') === s.is_active) &&
      (!q || [s.code, s.name, s.description ?? ''].some((x) => khongDau(x).includes(q))),
  );
}

/**
 * Cột lọc nhanh đã có trường khác dùng: `{ GRAND_TOTAL: 'Tổng thanh toán' }`. Bỏ qua trường đang
 * sửa — chính nó giữ cột thì vẫn chọn lại được.
 */
export function cotLocDaDung(fields: readonly OcrField[], boQuaId?: string): Partial<Record<DenormTarget, string>> {
  const ra: Partial<Record<DenormTarget, string>> = {};
  for (const f of fields) if (f.denorm_target && f.id !== boQuaId) ra[f.denorm_target] = f.label;
  return ra;
}

/**
 * Thân PATCH chỉ gồm khoá có giá trị khác bản gốc. Chuỗi được cắt khoảng trắng; chuỗi rỗng được coi
 * là `null` ở các khoá cho phép null (`nullable`), như backend hiểu "không đặt" — chuẩn hoá ở **cả
 * hai** phía để mô tả rỗng không bị gửi lại thành `null` khi người dùng không đụng tới.
 */
export function tinhPatch<T extends Record<string, unknown>>(
  goc: { [K in keyof T]?: unknown },
  moi: T,
  nullable: readonly (keyof T)[] = [],
): Partial<T> {
  const chuan = (k: keyof T, v: unknown) => {
    if (typeof v === 'string') {
      const cat = v.trim();
      return cat === '' && nullable.includes(k) ? null : cat;
    }
    return v === undefined && nullable.includes(k) ? null : v;
  };
  const ra: Partial<T> = {};
  for (const k of Object.keys(moi) as (keyof T)[]) {
    const v = chuan(k, moi[k]);
    if (!(k in goc) || v !== chuan(k, goc[k])) ra[k] = v as T[keyof T];
  }
  return ra;
}
